import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from 'src/store/middlewares/createAsyncThunkFromFetch';
import {
  addDevicesToGroupAPI,
  addFirmwareToGroupAPI,
  createDeviceGroupAPI,
  deleteDeviceGroupAPI,
  getDeviceGroupAPI,
  getDeviceGroupsAPI,
  getDevicesNotInGroup,
  getGroupDevicesAPI,
  getGroupFirmwaresAPI,
  removeDevicesFromGroupAPI,
  removeFirmwareFromGroupAPI,
  updateDeviceGroupAPI,
} from './deviceGroups.api';
import { DeviceGroup } from './deviceGroups.types';

const name = 'deviceGroups';

const groupsEntityAdapter = createEntityAdapter<AsyncEntity<DeviceGroup>>({
  selectId: g => g.id,
});

export const groupsSelectors = groupsEntityAdapter.getSelectors();

export const fetchDeviceGroups = createAsyncThunkFromFetch(`${name}/fetchDeviceGroups`, getDeviceGroupsAPI);
export const fetchDeviceGroup = createAsyncThunkFromFetch(`${name}/fetchDeviceGroup`, getDeviceGroupAPI);
export const deleteDeviceGroup = createAsyncThunkFromFetch(`${name}/deleteDeviceGroup`, deleteDeviceGroupAPI);
export const createDeviceGroup = createAsyncThunkFromFetch(`${name}/createDeviceGroup`, createDeviceGroupAPI);
export const updateDeviceGroup = createAsyncThunkFromFetch(`${name}/updateDeviceGroup`, updateDeviceGroupAPI);
export const fetchDeviceNotInGroup = createAsyncThunkFromFetch(`${name}/fetchDeviceNotInGroup`, getDevicesNotInGroup);

export const fetchGroupDevices = createAsyncThunkFromFetch(`${name}/fetchGroupDevices`, getGroupDevicesAPI);
export const addDeviceToGroup = createAsyncThunkFromFetch(`${name}/addDeviceToGroupDevice`, addDevicesToGroupAPI);
export const removeDevicesFromGroup = createAsyncThunkFromFetch(
  `${name}/removeDevicesFromGroup`,
  removeDevicesFromGroupAPI
);

export const fetchGroupFirmwares = createAsyncThunkFromFetch(`${name}/fetchGroupFirmwares`, getGroupFirmwaresAPI);
export const addFirmwareToGroup = createAsyncThunkFromFetch(`${name}/addFirmwareToGroup`, addFirmwareToGroupAPI);
export const removeFirmwareFromGroup = createAsyncThunkFromFetch(
  `${name}/removeFirmwareFromGroup`,
  removeFirmwareFromGroupAPI
);

const groupsSlice = createSlice({
  name,
  initialState: groupsEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    // Collections
    builder.addCase(fetchDeviceGroups.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchDeviceGroups.fulfilled, (state, { payload }) => {
      groupsEntityAdapter.setAll(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchDeviceGroup.fulfilled, (state, { payload }) => {
      groupsEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(fetchDeviceGroups.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteDeviceGroup.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteDeviceGroup.fulfilled, (state, { meta }) => {
      groupsEntityAdapter.removeOne(state, meta.arg.groupId);
    });
    builder.addCase(createDeviceGroup.fulfilled, (state, { payload }) => {
      groupsEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateDeviceGroup.fulfilled, (state, { meta, payload }) => {
      groupsEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(addDeviceToGroup.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(addDeviceToGroup.fulfilled, (state, { meta, payload }) => {
      const group = groupsSelectors.selectById(state, meta.arg.groupId) as DeviceGroup;
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: {
          devices: [...group.devices, ...payload],
          isPending: false,
        },
      });
    });
    builder.addCase(removeDevicesFromGroup.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(removeDevicesFromGroup.fulfilled, (state, { meta }) => {
      const group = groupsSelectors.selectById(state, meta.arg.groupId) as DeviceGroup;
      const devices = group.devices.filter(c => c.phyDeviceId !== meta.arg.deviceId);
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: {
          devices,
          isPending: false,
        },
      });
    });
    builder.addCase(fetchGroupDevices.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(fetchGroupDevices.fulfilled, (state, { meta, payload }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: {
          devices: [...payload],
          isPending: false,
        },
      });
    });
    builder.addCase(addFirmwareToGroup.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(addFirmwareToGroup.fulfilled, (state, { meta, payload }) => {
      const group = groupsSelectors.selectById(state, meta.arg.groupId) as DeviceGroup;
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: {
          firmwares: [...group.firmwares, payload],
          isPending: false,
        },
      });
    });
    builder.addCase(removeFirmwareFromGroup.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(removeFirmwareFromGroup.fulfilled, (state, { meta }) => {
      const group = groupsSelectors.selectById(state, meta.arg.groupId) as DeviceGroup;
      const firmwares = group.firmwares.filter(c => c.id !== meta.arg.firmwareId);
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: {
          firmwares,
          isPending: false,
        },
      });
    });
    builder.addCase(fetchGroupFirmwares.pending, (state, { meta }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: { isPending: true },
      });
    });
    builder.addCase(fetchGroupFirmwares.fulfilled, (state, { meta, payload }) => {
      groupsEntityAdapter.updateOne(state, {
        id: meta.arg.groupId,
        changes: {
          firmwares: [...payload],
          isPending: false,
        },
      });
    });
  },
});

const { reducer } = groupsSlice;

export default reducer;
